'use client';

/**
 * ChatDrawer — Chat as a peer surface that overlays Copilot.
 *
 * Step 4 of the Copilot/Chat chrome direction. See
 * `klyde_edge/docs/copilot_chat_chrome_concept.md`.
 *
 * The compound action that motivates this surface: *reading details
 * in Copilot, want to message the vendor without losing the details
 * context*. As a Copilot tab Chat shared chrome with read-mostly
 * detail surfaces and a tab-switch threw away the details view; as
 * a peer drawer the Copilot content stays mounted underneath, fully
 * preserved when the drawer dismisses.
 *
 * Surface spec (drawer-over):
 *   - Dock: same right-rail slot as Copilot (occupies the parent
 *     container via `absolute inset-0`)
 *   - Width: matches the Copilot ResizablePanel width (no canvas
 *     reflow on Chat open — drawer just covers Copilot)
 *   - Underneath: Copilot is fully occluded; pointer events and
 *     scroll on the Copilot region are blocked while the drawer is
 *     on top (achieved naturally by absolute-positioned solid
 *     background)
 *   - Dismissal: Esc, click on the dimmed canvas backdrop, or the
 *     close button in the drawer header
 *   - Send keeps the drawer open (composer behaviour comes from the
 *     embedded ChatPanel — unchanged from when it was a Copilot tab)
 *   - Mode change dismisses Chat (handled by layout-store's
 *     setActiveExplorerView clearing chatDrawerOpen)
 *
 * Composer drafts persist in the unified chat strategy regardless
 * of drawer visibility — dismissing and re-summoning on the same
 * selection brings the draft back. Persistence is the chat
 * strategy's responsibility, not this component's.
 *
 * @component_role: Chrome
 * @component_type: Layout Overlay
 * @domain: Frontend Shell / Messaging
 * @layer: presentation
 */

import { useEffect } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChatPanel } from './chat-panel';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';

export function ChatDrawer() {
  const chatDrawerOpen = useLayoutStore((s) => s.chatDrawerOpen);
  const setChatDrawerOpen = useLayoutStore((s) => s.setChatDrawerOpen);
  const { entityType, entityId } = useViewportSelection();

  // Esc dismisses Chat. Bind only when the drawer is mounted so we
  // don't pollute keypresses when Chat is closed.
  useEffect(() => {
    if (!chatDrawerOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setChatDrawerOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [chatDrawerOpen, setChatDrawerOpen]);

  if (!chatDrawerOpen) return null;

  return (
    <div
      className="absolute inset-0 z-30 flex flex-col bg-background border-l border-border"
      role="dialog"
      aria-modal="true"
      aria-label="Chat panel"
    >
      {/* Header — title + close. Mirrors the visual weight of the
          Copilot tab strip so the drawer feels native to the right
          rail. */}
      <div className="flex items-center justify-between border-b border-border px-3 py-2 bg-card">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <MessageSquare className="h-4 w-4 text-primary" />
          <span>Chat</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => setChatDrawerOpen(false)}
          aria-label="Close Chat"
          title="Close Chat (Esc)"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Body — embedded ChatPanel. Same component the Copilot tab
          rendered, just hosted in the drawer. ChatPanel handles the
          empty-state when entityType/entityId are null. */}
      <div className="flex-1 overflow-hidden">
        <ChatPanel entityType={entityType} entityId={entityId} />
      </div>
    </div>
  );
}
